"use client";

import { useState } from "react";
import { X, Check, ExternalLink, Key, Lock, Link as LinkIcon, LogIn } from "lucide-react";
import type { IntegrationType } from "@/types/integration";
import { supportsOAuth, getOAuthProvider, initiateOAuth } from "@/lib/integrations/oauth";

interface IntegrationConnectionFlowProps {
  integrationType: IntegrationType;
  integrationName: string;
  onClose: () => void;
  onConnect: (config: Record<string, any>) => Promise<void>;
}

// What each integration needs
const INTEGRATION_REQUIREMENTS: Partial<Record<IntegrationType, {
  type: "api-key" | "oauth" | "webhook" | "credentials";
  fields: Array<{
    key: string;
    label: string;
    type: "text" | "password" | "url";
    placeholder: string;
    required: boolean;
    helpText?: string;
    link?: string;
  }>;
  instructions: string[];
  docsLink?: string;
}>> = {
  shopify: {
    type: "oauth",
    fields: [
      {
        key: "storeUrl",
        label: "Store URL",
        type: "url",
        placeholder: "yourstore.myshopify.com",
        required: true,
        helpText: "Your Shopify store domain (without https://)",
      },
      {
        key: "apiKey",
        label: "API Key",
        type: "text",
        placeholder: "shpat_...",
        required: true,
        helpText: "Get this from Settings > Apps and sales channels > Develop apps",
        link: "https://help.shopify.com/en/manual/apps/app-types/custom-apps",
      },
      {
        key: "apiSecret",
        label: "API Secret",
        type: "password",
        placeholder: "Enter API secret",
        required: true,
      },
    ],
    instructions: [
      "1. Go to your Shopify admin dashboard",
      "2. Navigate to Settings > Apps and sales channels",
      "3. Click 'Develop apps' > 'Create an app'",
      "4. Name your app and configure Admin API access scopes",
      "5. Install the app and copy the API credentials",
    ],
    docsLink: "https://shopify.dev/docs/api/admin-rest",
  },
  stripe: {
    type: "api-key",
    fields: [
      {
        key: "apiKey",
        label: "Publishable Key",
        type: "text",
        placeholder: "pk_live_...",
        required: true,
        helpText: "Found in Stripe Dashboard > Developers > API keys",
        link: "https://dashboard.stripe.com/apikeys",
      },
      {
        key: "secretKey",
        label: "Secret Key",
        type: "password",
        placeholder: "sk_live_...",
        required: true,
        helpText: "Keep this secret! Never share it publicly.",
      },
    ],
    instructions: [
      "1. Go to https://dashboard.stripe.com/apikeys",
      "2. Copy your Publishable key (starts with pk_)",
      "3. Reveal and copy your Secret key (starts with sk_)",
      "4. Paste both keys below",
    ],
    docsLink: "https://stripe.com/docs/api",
  },
  paypal: {
    type: "credentials",
    fields: [
      {
        key: "clientId",
        label: "Client ID",
        type: "text",
        placeholder: "Enter Client ID",
        required: true,
        helpText: "From PayPal Developer Dashboard",
        link: "https://developer.paypal.com/dashboard",
      },
      {
        key: "clientSecret",
        label: "Client Secret",
        type: "password",
        placeholder: "Enter Client Secret",
        required: true,
      },
    ],
    instructions: [
      "1. Go to https://developer.paypal.com/dashboard",
      "2. Create a new app or use existing",
      "3. Copy Client ID and Client Secret",
      "4. Paste below",
    ],
    docsLink: "https://developer.paypal.com/docs/api/overview",
  },
  instagram: {
    type: "oauth",
    fields: [
      {
        key: "accessToken",
        label: "Access Token",
        type: "password",
        placeholder: "Enter access token",
        required: true,
        helpText: "Get from Facebook Developer Console",
        link: "https://developers.facebook.com/apps",
      },
    ],
    instructions: [
      "1. Go to https://developers.facebook.com/apps",
      "2. Create a new app or select existing",
      "3. Add Instagram Basic Display product",
      "4. Complete OAuth flow to get access token",
    ],
    docsLink: "https://developers.facebook.com/docs/instagram-basic-display-api",
  },
  facebook: {
    type: "oauth",
    fields: [
      {
        key: "pageAccessToken",
        label: "Page Access Token",
        type: "password",
        placeholder: "Enter page access token",
        required: true,
        helpText: "Get from Facebook Page Settings > Page Access Tokens",
        link: "https://developers.facebook.com/tools/explorer",
      },
    ],
    instructions: [
      "1. Go to https://developers.facebook.com/tools/explorer",
      "2. Select your app and page",
      "3. Generate Page Access Token",
      "4. Copy and paste below",
    ],
    docsLink: "https://developers.facebook.com/docs/graph-api",
  },
  twitter: {
    type: "api-key",
    fields: [
      {
        key: "apiKey",
        label: "API Key",
        type: "text",
        placeholder: "Enter API key",
        required: true,
        helpText: "From Twitter Developer Portal",
        link: "https://developer.twitter.com/en/portal/dashboard",
      },
      {
        key: "apiSecret",
        label: "API Secret",
        type: "password",
        placeholder: "Enter API secret",
        required: true,
      },
      {
        key: "accessToken",
        label: "Access Token",
        type: "password",
        placeholder: "Enter access token",
        required: true,
      },
      {
        key: "accessTokenSecret",
        label: "Access Token Secret",
        type: "password",
        placeholder: "Enter access token secret",
        required: true,
      },
    ],
    instructions: [
      "1. Go to https://developer.twitter.com/en/portal/dashboard",
      "2. Create a new app",
      "3. Generate API keys and access tokens",
      "4. Copy all credentials below",
    ],
    docsLink: "https://developer.twitter.com/en/docs/twitter-api",
  },
  youtube: {
    type: "oauth",
    fields: [
      {
        key: "apiKey",
        label: "API Key",
        type: "text",
        placeholder: "Enter YouTube API key",
        required: true,
        helpText: "From Google Cloud Console",
        link: "https://console.cloud.google.com/apis/credentials",
      },
      {
        key: "oauthToken",
        label: "OAuth Token",
        type: "password",
        placeholder: "Enter OAuth token",
        required: false,
        helpText: "Required for uploading videos",
      },
    ],
    instructions: [
      "1. Go to https://console.cloud.google.com/apis/library",
      "2. Enable YouTube Data API v3",
      "3. Create API key in Credentials",
      "4. For uploads, complete OAuth flow",
    ],
    docsLink: "https://developers.google.com/youtube/v3",
  },
  zapier: {
    type: "webhook",
    fields: [
      {
        key: "webhookUrl",
        label: "Zapier Webhook URL",
        type: "url",
        placeholder: "https://hooks.zapier.com/hooks/catch/...",
        required: true,
        helpText: "Create a Zap with Webhooks by Zapier trigger",
        link: "https://zapier.com/apps/webhook/integrations",
      },
    ],
    instructions: [
      "1. Go to Zapier and create a new Zap",
      "2. Choose 'Webhooks by Zapier' as trigger",
      "3. Select 'Catch Hook'",
      "4. Copy the webhook URL and paste below",
    ],
    docsLink: "https://zapier.com/help/create/catch-webhooks-in-zaps",
  },
  webhook: {
    type: "webhook",
    fields: [
      {
        key: "webhookUrl",
        label: "Webhook URL",
        type: "url",
        placeholder: "https://your-api.com/webhook",
        required: true,
        helpText: "Your custom endpoint that accepts POST requests",
      },
      {
        key: "secret",
        label: "Webhook Secret (optional)",
        type: "password",
        placeholder: "Enter secret for verification",
        required: false,
        helpText: "Optional: Secret key for webhook verification",
      },
    ],
    instructions: [
      "1. Set up an endpoint on your server",
      "2. Ensure it accepts POST requests",
      "3. Paste the full URL below",
      "4. Optionally add a secret for security",
    ],
  },
  tiktok: {
    type: "oauth",
    fields: [
      {
        key: "accessToken",
        label: "Access Token",
        type: "password",
        placeholder: "Enter TikTok access token",
        required: true,
        helpText: "Get from TikTok Developer Portal",
        link: "https://developers.tiktok.com",
      },
    ],
    instructions: [
      "1. Go to https://developers.tiktok.com",
      "2. Create a new app",
      "3. Complete OAuth flow",
      "4. Copy access token",
    ],
    docsLink: "https://developers.tiktok.com/doc",
  },
  snapchat_ads: {
    type: "oauth",
    fields: [
      {
        key: "clientId",
        label: "Client ID",
        type: "text",
        placeholder: "Enter Client ID",
        required: true,
        helpText: "From Snapchat Marketing API",
        link: "https://business.snapchat.com",
      },
      {
        key: "clientSecret",
        label: "Client Secret",
        type: "password",
        placeholder: "Enter Client Secret",
        required: true,
      },
    ],
    instructions: [
      "1. Go to Snapchat Business Manager",
      "2. Create a new app",
      "3. Get Client ID and Secret",
      "4. Complete OAuth flow",
    ],
    docsLink: "https://marketingapi.snapchat.com/docs",
  },
  // Add more integrations as needed
  woocommerce: {
    type: "credentials",
    fields: [
      {
        key: "storeUrl",
        label: "Store URL",
        type: "url",
        placeholder: "https://yourstore.com",
        required: true,
      },
      {
        key: "consumerKey",
        label: "Consumer Key",
        type: "text",
        placeholder: "ck_...",
        required: true,
        helpText: "From WooCommerce > Settings > Advanced > REST API",
      },
      {
        key: "consumerSecret",
        label: "Consumer Secret",
        type: "password",
        placeholder: "cs_...",
        required: true,
      },
    ],
    instructions: [
      "1. Go to WooCommerce > Settings > Advanced > REST API",
      "2. Click 'Add Key'",
      "3. Set permissions to 'Read/Write'",
      "4. Copy Consumer Key and Secret",
    ],
    docsLink: "https://woocommerce.github.io/woocommerce-rest-api-docs",
  },
  bigcommerce: {
    type: "oauth",
    fields: [
      {
        key: "storeHash",
        label: "Store Hash",
        type: "text",
        placeholder: "Enter store hash",
        required: true,
      },
      {
        key: "accessToken",
        label: "Access Token",
        type: "password",
        placeholder: "Enter access token",
        required: true,
      },
    ],
    instructions: [
      "1. Go to BigCommerce Developer Portal",
      "2. Create OAuth app",
      "3. Complete OAuth flow",
      "4. Get store hash and access token",
    ],
    docsLink: "https://developer.bigcommerce.com/api-docs",
  },
  square: { type: "oauth", fields: [], instructions: [] },
  etsy: { type: "oauth", fields: [], instructions: [] },
  amazon: { type: "oauth", fields: [], instructions: [] },
  ebay: { type: "oauth", fields: [], instructions: [] },
  magento: { type: "oauth", fields: [], instructions: [] },
  prestashop: { type: "oauth", fields: [], instructions: [] },
  opencart: { type: "oauth", fields: [], instructions: [] },
  squarespace: { type: "oauth", fields: [], instructions: [] },
  wix: { type: "oauth", fields: [], instructions: [] },
};

export default function IntegrationConnectionFlow({
  integrationType,
  integrationName,
  onClose,
  onConnect,
}: IntegrationConnectionFlowProps) {
  const requirements = INTEGRATION_REQUIREMENTS[integrationType] || {
    type: "api-key",
    fields: [],
    instructions: ["Configuration details coming soon."],
  };

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionMethod, setConnectionMethod] = useState<"oauth" | "api-key">("oauth");
  
  // Check if OAuth is supported
  const hasOAuth = supportsOAuth(integrationType);
  const oauthProvider = getOAuthProvider(integrationType);

  async function handleConnect() {
    setError(null);
    
    // Validate required fields
    const missingFields = requirements.fields
      .filter((f) => f.required && !formData[f.key])
      .map((f) => f.label);
    
    if (missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }

    setConnecting(true);
    try {
      await onConnect(formData);
      // Success - parent component will handle closing
    } catch (err: any) {
      setError(err.message || "Failed to connect. Please check your credentials.");
    } finally {
      setConnecting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Connect {integrationName}</h2>
            <p className="text-sm text-gray-600 mt-1">Follow the steps below to connect your account</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Connection Method Toggle */}
        {hasOAuth && (
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Connection Method:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setConnectionMethod("oauth")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    connectionMethod === "oauth"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <LogIn className="w-4 h-4 inline mr-2" />
                  Sign in with {oauthProvider?.name}
                </button>
                <button
                  onClick={() => setConnectionMethod("api-key")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    connectionMethod === "api-key"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <Key className="w-4 h-4 inline mr-2" />
                  Use API Key
                </button>
              </div>
            </div>
            {connectionMethod === "oauth" && (
              <p className="mt-3 text-sm text-gray-600">
                Quick and secure. Sign in with your {oauthProvider?.name} account to connect instantly.
              </p>
            )}
          </div>
        )}

        {/* Instructions */}
        {connectionMethod === "api-key" && (
          <div className="p-6 bg-blue-50 border-b border-blue-100">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Setup Instructions
            </h3>
          <ol className="space-y-2 text-sm text-gray-700">
            {requirements.instructions.map((step, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-medium text-blue-600">{step.split(" ")[0]}</span>
                <span>{step.substring(step.indexOf(" ") + 1)}</span>
              </li>
            ))}
          </ol>
          {requirements.docsLink && (
            <a
              href={requirements.docsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              View API Documentation
            </a>
          )}
          </div>
        )}

        {/* OAuth Connect Button */}
        {connectionMethod === "oauth" && hasOAuth && (
          <div className="p-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Connect with {oauthProvider?.name}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Sign in securely with your {oauthProvider?.name} account. We'll never store your password.
              </p>
              <button
                onClick={() => {
                  const state = `${integrationType}:${Date.now()}`;
                  localStorage.setItem("oauth_state", state);
                  initiateOAuth(oauthProvider!.id, state);
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2 mx-auto"
              >
                <LogIn className="w-5 h-5" />
                Sign in with {oauthProvider?.name}
              </button>
              <p className="text-xs text-gray-500 mt-4">
                By connecting, you agree to share necessary permissions for this integration to work.
              </p>
            </div>
          </div>
        )}

        {/* Form (API Key Method) */}
        {connectionMethod === "api-key" && (
          <div className="p-6 space-y-4">
          {requirements.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="relative">
                <input
                  type={field.type === "password" ? "password" : field.type === "url" ? "url" : "text"}
                  value={formData[field.key] || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, [field.key]: e.target.value })
                  }
                  placeholder={field.placeholder}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {field.link && (
                  <a
                    href={field.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-700"
                    title="Get credentials"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
              {field.helpText && (
                <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
              )}
            </div>
          ))}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex gap-3 rounded-b-2xl">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          {connectionMethod === "api-key" && (
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            >
              {connecting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Connect
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

